import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
	generateRfcService,
	validateCurpService,
} from '../../../shared/services/rfc.service';
import {
	CURP_EXTRACTION_TERMINAL_STATUSES,
	curpExtractionQueryKey,
	getCurpExtractionService,
} from '../../../shared/services/curpExtraction.service';
import {
	parseFullName,
	type ParsedFullName,
} from '../../../shared/utils/parseFullName.util';
import { REGEX } from '../../../shared/constants/regex';
import { useOnboarding, useUpdateOnboarding } from './useOnboarding';
import { useProfileFullName } from './useProfileFullName';
import { useCurpExtraction } from './useCurpExtraction';
import {
	buildCurpValidatedAnswer,
	buildRfcAnswer,
	buildRfcValidatedByCurpAnswer,
	getValidatedIdentity,
	isCurpAlreadyValidated,
} from '../utils/validationCache.util';

const CURP_LENGTH = 18;
/** Cuánto espera «Generar mi RFC» a que el SAT devuelva el RFC de la CURP
 * antes de caer al cálculo por nombre. */
const SAT_RFC_WAIT_MS = 40_000;
const SAT_RFC_CHECK_INTERVAL_MS = 2_500;

/** `sat_curp` = el RFC que el SAT tiene para la CURP (`rfc_valid === true`) ·
 * `calculated` = el cálculo por nombre y CURP, cuando el SAT no contestó. */
export type RfcGenerationSource = 'sat_curp' | 'calculated';

export type RfcGenerationResult = {
	rfc: string;
	curp: string;
	name: ParsedFullName;
	source: RfcGenerationSource;
};

/** `source` = el camino que dio el RFC o que falló; null si falló la
 * validación de nombre o CURP antes de intentar alguno. */
export type RfcGenerationOutcome = {
	result: RfcGenerationResult | null;
	source: RfcGenerationSource | null;
};

const wait = (ms: number) =>
	new Promise<void>((resolve) => {
		setTimeout(resolve, ms);
	});

type UseRfcGeneratorOptions = {
	initialCurp?: string;
	initialFullName?: string;
};

const describeCurpValidationError = (code?: string): string => {
	switch (code) {
		case 'NAME_MISMATCH':
			return 'El CURP no coincide con el nombre que escribiste.';
		case 'INVALID_CHECK_DIGIT':
			return 'El CURP no es válido.';
		default:
			return 'No pudimos validar tu CURP, intenta de nuevo.';
	}
};

// Encapsulates the "generate my RFC from CURP + full name" logic shared by the
// SAT linking drawer (RfcHelpDrawer) and the onboarding consult-RFC flow:
// name/CURP state, validation, the validate-then-generate call, and persisting
// the result. The name is saved to the user profile (via useProfileFullName);
// only the CURP/RFC validation cache lives in the onboarding record. Components
// own their own view transitions; this hook owns the data.
export const useRfcGenerator = ({
	initialCurp = '',
	initialFullName = '',
}: UseRfcGeneratorOptions = {}) => {
	const { data: onboarding } = useOnboarding();
	const { mutateAsync: updateOnboarding } = useUpdateOnboarding();
	const queryClient = useQueryClient();
	const { triggerExtraction } = useCurpExtraction();

	// Identity lock: once the CURP is validated the server freezes it, so the
	// field becomes read-only and seeds from the validated identity.
	const lockedIdentity = getValidatedIdentity(onboarding);
	const isCurpLocked = lockedIdentity !== null;

	// The name is owned by the profile (read from it, written back to it). The
	// seed (caller's initial value / the validated identity) is only used until
	// the profile loads or when it has no name yet.
	const {
		fullName,
		setFullName,
		resetName,
		saveToProfile,
	} = useProfileFullName({
		fallback: initialFullName || lockedIdentity?.fullName || '',
	});

	const [curpDraft, setCurpDraft] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isSearchingSat, setIsSearchingSat] = useState(false);
	const [nameError, setNameError] = useState<string | null>(null);
	const [curpError, setCurpError] = useState<string | null>(null);
	const [generalError, setGeneralError] = useState<string | null>(null);
	const [generatedRfc, setGeneratedRfc] = useState<string | null>(null);
	const [generatedSource, setGeneratedSource] =
		useState<RfcGenerationSource | null>(null);
	const [parsedName, setParsedName] = useState<ParsedFullName | null>(null);

	const curp = curpDraft ?? (lockedIdentity?.curp || initialCurp);

	const onNameChange = (value: string) => {
		setFullName(value);
		if (nameError) setNameError(null);
		if (generalError) setGeneralError(null);
	};

	const onCurpChange = (value: string) => {
		if (isCurpLocked) return;
		const sanitized = value
			.toUpperCase()
			.replace(/[^A-Z0-9]/g, '')
			.slice(0, CURP_LENGTH);
		setCurpDraft(sanitized);
		if (curpError) setCurpError(null);
		if (generalError) setGeneralError(null);
	};

	const clearResult = () => {
		setGeneratedRfc(null);
		setGeneratedSource(null);
		setParsedName(null);
	};

	const reset = () => {
		resetName();
		setCurpDraft(null);
		setNameError(null);
		setCurpError(null);
		setGeneralError(null);
		setGeneratedRfc(null);
		setGeneratedSource(null);
		setParsedName(null);
		setIsLoading(false);
		setIsSearchingSat(false);
	};

	// Dispara la extracción por CURP (la misma del banner fiscal) y espera hasta
	// SAT_RFC_WAIT_MS a que el SAT devuelva un RFC válido. Lee por la misma llave
	// que el polling del contexto, así que no duplica consultas en vuelo.
	// Cualquier falla cuenta como «el SAT no contestó».
	const findSatRfc = async (curpValue: string): Promise<string | null> => {
		setIsSearchingSat(true);
		try {
			await triggerExtraction(curpValue, 'consult_rfc');
			const deadline = Date.now() + SAT_RFC_WAIT_MS;
			for (;;) {
				const state = await queryClient.fetchQuery({
					queryKey: curpExtractionQueryKey(curpValue),
					queryFn: () => getCurpExtractionService(curpValue),
					staleTime: SAT_RFC_CHECK_INTERVAL_MS,
				});
				const satRfc = (state?.rfc ?? '').trim().toUpperCase();
				if (state?.rfc_valid === true && satRfc) return satRfc;
				if (state && CURP_EXTRACTION_TERMINAL_STATUSES.includes(state.status)) {
					return null;
				}
				if (Date.now() + SAT_RFC_CHECK_INTERVAL_MS > deadline) return null;
				await wait(SAT_RFC_CHECK_INTERVAL_MS);
			}
		} catch {
			return null;
		} finally {
			setIsSearchingSat(false);
		}
	};

	// Validates the name + CURP, then asks the SAT for the CURP's RFC and falls
	// back to the name-based calculation. Returns the result on success (and
	// stores it + parsedName in state), or a null result on any validation or
	// network failure, surfacing the reason via the error fields. The returned
	// object lets callers persist immediately without waiting for the state
	// update (which only lands on the next render).
	const generate = async (): Promise<RfcGenerationOutcome> => {
		setNameError(null);
		setCurpError(null);
		setGeneralError(null);

		const parsed = parseFullName(fullName);
		if (!parsed) {
			setNameError(
				'Escribe tu nombre completo: nombres y apellidos (mínimo 3 palabras).',
			);
			return { result: null, source: null };
		}

		if (curp.length !== CURP_LENGTH || !REGEX.curpRegex.test(curp)) {
			setCurpError('El CURP debe tener 18 caracteres válidos.');
			return { result: null, source: null };
		}

		setIsLoading(true);
		let source: RfcGenerationSource | null = null;
		try {
			if (!isCurpAlreadyValidated(onboarding, curp, parsed)) {
				const curpValidation = await validateCurpService({
					curp,
					nombre: parsed.nombre,
					apellido_paterno: parsed.apellido_paterno,
					apellido_materno: parsed.apellido_materno,
				});

				if (!curpValidation?.valid) {
					if (curpValidation?.code === 'RATE_LIMIT') {
						setGeneralError(
							'Alcanzaste el límite diario de validaciones. Inténtalo mañana.',
						);
					} else {
						setCurpError(describeCurpValidationError(curpValidation?.code));
					}
					return { result: null, source: null };
				}

				await updateOnboarding({
					answers: {
						curp_validated: buildCurpValidatedAnswer(curp, parsed),
					},
				});
			}

			const satRfc = await findSatRfc(curp);
			source = satRfc ? 'sat_curp' : 'calculated';
			const rfc =
				satRfc ??
				(
					await generateRfcService({
						curp,
						nombre: parsed.nombre,
						apellido_paterno: parsed.apellido_paterno,
						apellido_materno: parsed.apellido_materno,
					})
				).rfc;

			setGeneratedRfc(rfc);
			setGeneratedSource(source);
			setParsedName(parsed);
			return { result: { rfc, curp, name: parsed, source }, source };
		} catch (error) {
			const status = (error as { response?: { status?: number } })?.response
				?.status;
			if (status === 429) {
				setGeneralError(
					'Alcanzaste el límite diario de validaciones. Inténtalo mañana.',
				);
			} else {
				setGeneralError('No pudimos generar tu RFC, intenta de nuevo.');
			}
			return { result: null, source };
		} finally {
			setIsLoading(false);
		}
	};

	// Saves the name to the profile (via useProfileFullName) and the CURP/RFC +
	// validation cache into the onboarding record. The name lives only in the
	// profile now. Accepts an explicit result so callers can persist right after
	// generate() without waiting for the state update; falls back to state when
	// called with no args. Returns the persisted result, or null when there's
	// nothing to persist OR when the onboarding-record write failed: rfc-ready
	// reads the RFC back from that record, so the caller must not advance there
	// when it didn't land.
	const persist = async (
		override?: RfcGenerationResult,
	): Promise<Omit<RfcGenerationResult, 'source'> | null> => {
		const rfcValue = override?.rfc ?? generatedRfc;
		const nameValue = override?.name ?? parsedName;
		const curpValue = override?.curp ?? curp;
		if (!rfcValue || !nameValue) return null;

		const recordPersisted = updateOnboarding({
			answers: {
				curp: { label: 'CURP', value: curpValue },
				rfc: buildRfcAnswer(rfcValue),
				...(curpValue
					? { rfc_validated: buildRfcValidatedByCurpAnswer(rfcValue, curpValue) }
					: {}),
			},
		})
			.then(() => true)
			.catch(() => false);

		const [persisted] = await Promise.all([
			recordPersisted,
			saveToProfile(nameValue),
		]);
		if (!persisted) return null;
		return { rfc: rfcValue, curp: curpValue, name: nameValue };
	};

	// Validates name + CURP format and stages them — name to the profile, CURP to
	// the onboarding record — WITHOUT generating yet. Lets the form advance to a
	// review screen that reads both back from those persisted sources (and a
	// later "edit" returns to a correctly-prefilled form). Returns true on
	// success; on a format error it surfaces it via nameError/curpError.
	const saveInputs = async (): Promise<boolean> => {
		setNameError(null);
		setCurpError(null);
		setGeneralError(null);

		const parsed = parseFullName(fullName);
		if (!parsed) {
			setNameError(
				'Escribe tu nombre completo: nombres y apellidos (mínimo 3 palabras).',
			);
			return false;
		}
		if (curp.length !== CURP_LENGTH || !REGEX.curpRegex.test(curp)) {
			setCurpError('El CURP debe tener 18 caracteres válidos.');
			return false;
		}

		setIsLoading(true);
		try {
			await Promise.all([
				saveToProfile(parsed),
				updateOnboarding({
					answers: { curp: { label: 'CURP', value: curp } },
				}).catch(() => null),
			]);
			return true;
		} finally {
			setIsLoading(false);
		}
	};

	return {
		fullName,
		curp,
		isLoading,
		isSearchingSat,
		isCurpLocked,
		nameError,
		curpError,
		generalError,
		generatedRfc,
		generatedSource,
		parsedName,
		onNameChange,
		onCurpChange,
		generate,
		persist,
		saveInputs,
		clearResult,
		reset,
	};
};
